<template>
  <section>
    <div v-if="categoryData">
      <data-field
        v-for="(value, key) in categoryData.metadata"
        :key="key"
        :item="value"
        :fieldKey="key"
        @changeTextValue="onInput"
      />
      <v-btn
        color="success"
        @click="save"
        :disabled="!isWriteAvailable"
      >Update</v-btn>
    </div>
    <v-dialog v-model="showGallery">
      <galleries-list
        :modalView="true"
        :pickedImage="setImage"
      />
    </v-dialog>
    <v-dialog persistent width="400" v-model="isCreateDialogVisible">
      <v-card>
        <v-card-title>Label</v-card-title>
        <v-card-text>
          <v-form class="my-3">
            <content-field
              label="Label"
              v-model="newItemData.label"
              :error="$v.newItemData.label"
            />
            <v-select
              label="Item type"
              :items="availableTypes"
              v-model="newItemData.type"
              :error="isSelectError"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn class="green white--text" @click="addNewItemToArray">Add</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import ContentField from '@/components/shared/ContentField'
import ContentEditor from '@/components/shared/ContentEditor'
import GalleriesList from '@/components/pages/Gallery/GalleriesList'
import DataField from './dataField'
import { required, email, numeric, url } from 'vuelidate/lib/validators'
import { mapGetters, mapActions } from 'vuex'
import { clone, propEq, isEmpty, path, pathOr, assocPath, dissocPath } from 'ramda'
import mixins from '@/utils/mixins'

const defaultFormData = {
  label: '',
  content: '',
  type: ''
}

export default {
  name: "StaticPageContent",
  mixins: [mixins.isWriteAvailable],
  components: {
    ContentField,
    ContentEditor,
    GalleriesList,
    DataField
  },
  data () {
    return {
      categoryData: null,
      showGallery: false,
      selectImageKey: '',
      parentKey: '',
      newItemData: {
        label: '',
        content: '',
        type: ''
      },
      isCreateDialogVisible: false,
      // DON'T DELETE! need for isWriteAvailable mixin
      scopeForWrite: 'general_settings:write',
    }
  },
  validations: {
    newItemData: {
      label: { required },
      type: { required }
    }
  },
  watch: {
    parentKey () {
      if (this.getArrayLength(this.parentKey)) {
        const valuesOfArray = this.getValuesOfContent(this.parentKey)
        const typeOfFirstItem = valuesOfArray[0].type
        this.newItemData.type = typeOfFirstItem
      } else {
        this.newItemData.type = ''
      }
    },
    staticContent () {
      this.assignContentByRoute()
    },
    '$route.params.page' () {
      this.assignContentByRoute()
    },
    availableTypes () {
      // length of full list field types.
      const fullListOfTypesLength = 4
      this.newItemData.type = this.availableTypes.length === fullListOfTypesLength ? '' : this.availableTypes
    }
  },
  computed: {
    ...mapGetters(['staticContent']),
    isSelectError () {
      return this.$v.newItemData.type.$invalid && this.$v.newItemData.$dirty
    },
    availableTypes () {
      if (this.getArrayLength(this.parentKey)) {
        const valuesOfArray = this.getValuesOfContent(this.parentKey)
        const typeOfFirstItem = valuesOfArray[0].type
        return [typeOfFirstItem]
      } else {
        return ['input', 'url', 'textarea', 'image']
      }
    }
  },
  methods: {
    ...mapActions(['updateStaticContent', 'fetchStaticContent']),
    assignContentByRoute () {
      const route = this.$route.params.page
      const data = this.staticContent.find(propEq('name', route))
      this.categoryData = clone(data)
    },
    async save () {
      await this.updateStaticContent(this.categoryData)
      this.fetchStaticContent()
    },
    hideGallery () {
      this.showGallery = false
      this.selectImageKey = ''
    },
    setImage (item) {
      const meta = clone(this.categoryData.metadata)
      const imageUrl = pathOr('', ['imagesUrls', 'originalImageUrl'], item)

      // if it's first level of nesting
      if (meta.hasOwnProperty(this.selectImageKey)) {
        const value = imageUrl
        const parentKey = this.selectImageKey
        this.assignContent(value, parentKey)
        this.hideGallery()
      } else {

      // if it's nested item of array
        for (const key in meta) {
          if (!!this.isFieldArray(key) && this.isArrayContainsChild(key, this.selectImageKey)) {
            const parentKey = key
            const childKey = this.selectImageKey
            const value = imageUrl
            this.assignContent(value, parentKey, childKey)
            this.hideGallery()
            break
          }
        }
      }
    },
    assignContent (value, parentKey, childKey) {
      let pathToAssoc = ['metadata', parentKey, 'content']
      if (childKey) {
        this.categoryData.metadata[parentKey].content[childKey].content = value
      } else {
        this.categoryData.metadata[parentKey].content = value
      }
    },
    openGallery () {
      this.showGallery = true
    },
    closeGallery () {
      this.showGallery = false
    },
    isFieldArray (key) {
      const field = this.getParentField(key)
        return field ? field.type === 'array' : false
    },
    isFieldObject(pathToAssoc) {
      return typeof path(pathToAssoc, this.categoryData) === 'object'
    },
    isArrayContainsChild (parentKey, childKey) {
      const field = this.getParentField(parentKey)
      return field ? field.content.hasOwnProperty(childKey) : false
    },
    getParentField (key) {
      return path(['metadata', key], this.categoryData)
    },
    onInput ({ value, key }) {
      const parentKey = key
      const childKey = path(['key'], value)
      const assignmentValue = pathOr(value, ['value'], value)
      this.assignContent(assignmentValue, parentKey, childKey)
    },
    generateNewItemKey (parentKey) {
      let maxIndex
      const keys = this.getKeysOfContent(parentKey)
      const mapLastNumber = name => {
        const lastNumberRegex = /\d+$/g
        const matchedNumbers = name.match(lastNumberRegex)
        const defaultIndexValue = 0
        return matchedNumbers ? parseInt(matchedNumbers[0]) : defaultIndexValue
      }
      if (isEmpty(keys)) {
        maxIndex = 0
      } else {
        const indexesArray = keys.map(mapLastNumber)
        maxIndex = Math.max(...indexesArray)
      }

      return `${parentKey}Item${maxIndex + 1}`
    },
    addNewItemToArray () {
      this.$v.$touch()
      if (this.$v.newItemData.$invalid) return
      const newItemKey = this.generateNewItemKey(this.parentKey)
      this.categoryData = assocPath(
        ['metadata', this.parentKey, 'content', newItemKey],
        {...this.newItemData},
        this.categoryData
      )
      this.isCreateDialogVisible = false
      this.newItemData = clone(defaultFormData)
      this.$v.$reset()
    },
    resetImage (resetKey) {
      if (pathOr('', ['metadata', resetKey, 'content'] , this.categoryData)) {
        this.categoryData.metadata[resetKey].content = ''
      } else {
        for (const key in this.categoryData.metadata) {
          if (this.isFieldArray(key)) {
          this.categoryData.metadata[key].content[resetKey].content = ''
          }
        }
      }
    },
    delteItemFromArray (deleteKey) {
      for (const key in this.categoryData.metadata) {
        if (this.isFieldArray(key) && this.isArrayContainsChild(key, deleteKey)) {
          this.categoryData = dissocPath(['metadata', key, 'content', deleteKey], this.categoryData)
          break
        }
      }
    },
    getKeysOfContent (parentKey) {
      const fieldContent = pathOr({}, ['metadata', parentKey, 'content'], this.categoryData)
      return Object.keys(fieldContent)
    },
    getValuesOfContent (parentKey) {
      const fieldContent = pathOr({}, ['metadata', parentKey, 'content'], this.categoryData)
      return Object.values(fieldContent)
    },
    getArrayLength (parentKey) {
      return this.getKeysOfContent(parentKey).length
    }
  },
  created () {
    this.$bus.$on('openGallery', key => {
      this.selectImageKey = key
      this.showGallery = true
    })

    this.$bus.$on('closeGallery', () => {
      this.selectImageKey = ''
      this.showGallery = false
    })

    this.$bus.$on('createNewItem', key => {
      this.isCreateDialogVisible = true
      this.parentKey = key
    })

    this.$bus.$on('delteItemFromArray', key => {
      this.delteItemFromArray(key)
    })

    this.$bus.$on('resetImage', this.resetImage)
  },
  beforeDestroy () {
    this.$bus.$off('openGallery')
    this.$bus.$off('resetImage')
    this.$bus.$off('closeGallery')
    this.$bus.$off('createNewItem')
    this.$bus.$off('delteItemFromArray')
  }
}
</script>



// WEBPACK FOOTER //
// src/components/pages/StaticPages/pageContent.vue